import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const CreatePostWithOutCat = () => {
  const [title, setTitle] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const userDataString = localStorage.getItem("Login_User_Community_All_Data");
  const userData = JSON.parse(userDataString);
  const token = userData.token;
  const roleId = userData.role;
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [errors, setErrors] = useState({});
  const quillRef = useRef(null);
  const quillContainerRef = useRef(null);
  const [pollDialogOpen, setPollDialogOpen] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState([""]);

  useEffect(() => {
    if (quillContainerRef.current && !quillRef.current) {
      quillRef.current = new Quill(quillContainerRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"],
            ["clean"],
          ],
        },
      });
    }
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/getCategoriesByRoleId/${roleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handlePost = async () => {
    const newErrors = {};

    if (!title.trim()) newErrors.title = "Title is required.";
    if (!selectedCategory) newErrors.category = "Category is required.";
    if (!selectedSubcategory) newErrors.subcategory = "Subcategory is required.";

    const htmlContent = quillRef.current ? quillRef.current.root.innerHTML : "";
    const textContent = quillRef.current ? quillRef.current.getText().trim() : "";

    if (!textContent) {
      newErrors.content = "Content is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const newPost = {
      cat_id: selectedSubcategory,
      title: title,
      post_details: textContent,
      user_id: userData.id,
    };

    console.log("newPost:", newPost);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/postSave`, newPost, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const insertedId = response.data.insertedId;

      Swal.fire({
        icon: "success",
        title: "Post Created!",
        text: "Your post has been successfully created.",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      setTitle("");
      if (quillRef.current) {
        quillRef.current.setText("");
      }
      setSelectedCategory("");
      setSelectedSubcategory("");
      navigate(`/provider-user/post-details/${insertedId}`);
    } catch (error) {
      console.error("Error saving post:", error.response?.data || error.message);
      Swal.fire({
        icon: "error",
        title: "Failed to Create Post",
        text: "An error occurred while creating your post. Please try again.",
      });
    }
  };

  const openPollDialog = () => {
    setPollDialogOpen(true);
  };

  const closePollDialog = () => {
    setPollDialogOpen(false);
    setPollQuestion("");
    setPollOptions(["", ""]);
  };

  const addPollOption = () => {
    setPollOptions([...pollOptions, ""]);
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const insertPollIntoEditor = () => {
    if (!pollQuestion.trim()) {
      alert("Poll question cannot be empty!");
      return;
    }

    const pollText =
      `POLL: ${pollQuestion}\n\n` +
      pollOptions.map((opt, index) => `Option ${index + 1}: ${opt}`).join("\n");

    if (quillRef.current) {
      const range = quillRef.current.getSelection(true);
      quillRef.current.insertText(range ? range.index : 0, `\n${pollText}\n\n`);
    }

    closePollDialog();
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ backgroundColor: "#2196f3", color: "#fff", padding: "8px 0" }}
        gutterBottom
      >
        Create New Post
      </Typography>
      <TextField
        label="Title"
        variant="outlined"
        fullWidth
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{ marginBottom: "15px" }}
        required
        error={!!errors.title}
        helperText={errors.title}
      />
      <FormControl fullWidth sx={{ marginBottom: "15px" }} required>
        <InputLabel>Category</InputLabel>
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </Select>
        {errors.category && <Typography color="error">{errors.category}</Typography>}
      </FormControl>
      {selectedCategory && (
        <FormControl fullWidth sx={{ marginBottom: "15px" }} required>
          <InputLabel>Subcategory</InputLabel>
          <Select
            value={selectedSubcategory}
            onChange={(e) => setSelectedSubcategory(e.target.value)}
          >
            {categories
              .find((category) => category.id === selectedCategory)
              ?.subcategories.map((subcategory) => (
                <MenuItem key={subcategory.id} value={subcategory.id}>
                  {subcategory.name}
                </MenuItem>
              ))}
          </Select>
          {errors.subcategory && <Typography color="error">{errors.subcategory}</Typography>}
        </FormControl>
      )}

      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: "5px",
          padding: "10px",
          minHeight: "300px",
          backgroundColor: "#fff",
        }}
      >
        <div
          ref={quillContainerRef}
          style={{ minHeight: "200px", backgroundColor: "#fff", borderRadius: "4px" }}
        />
      </div>
      {errors.content && <Typography color="error">{errors.content}</Typography>}

      <Dialog open={pollDialogOpen} onClose={closePollDialog}>
        <DialogTitle>Add Poll</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Poll Question"
            value={pollQuestion}
            onChange={(e) => setPollQuestion(e.target.value)}
            sx={{ marginBottom: "10px" }}
          />
          {pollOptions.map((option, index) => (
            <TextField
              key={index}
              fullWidth
              label={`Option ${index + 1}`}
              value={option}
              onChange={(e) => updatePollOption(index, e.target.value)}
              sx={{ marginBottom: "10px" }}
            />
          ))}
          <Button onClick={addPollOption} startIcon={<AddCircleOutlineIcon />}>
            Add Option
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePollDialog}>Cancel</Button>
          <Button onClick={insertPollIntoEditor} color="primary">
            Insert Poll
          </Button>
        </DialogActions>
      </Dialog>

      <Button onClick={openPollDialog} variant="contained" color="primary">
        Add Poll
      </Button>

      <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
        <Button onClick={handlePost} variant="contained" disabled={loading}>
          Create Post
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePostWithOutCat;
